/**
 * Central game configuration constants.
 * All tunable gameplay values live here for easy balancing.
 */

// --- Display ---
export const GAME_WIDTH = 800;
export const GAME_HEIGHT = 600;

// --- Maze Grid ---
export const TILE_SIZE = 24;
export const BASE_MAZE_COLS = 15;
export const BASE_MAZE_ROWS = 13;
export const MAZE_GROWTH_COLS = 2;
export const MAZE_GROWTH_ROWS = 2;
export const MAX_MAZE_COLS = 41;
export const MAX_MAZE_ROWS = 31;

// --- Player ---
export const PLAYER_MOVE_DURATION = 130;   // ms per tile-to-tile tween
export const PLAYER_SIZE = 16;             // px drawn size for the player sprite

// --- Health ---
export const MAX_HEALTH = 100;
export const HEALTH_DRAIN_PER_MOVE = 1;
export const INITIAL_LIVES = 3;

// --- Food ---
export const FOOD_HEAL_AMOUNT = 25;             // default / generic heal (fallback)
export const FOOD_HEAL_FRUIT = 15;              // fruit heals the least
export const FOOD_HEAL_TUNA = 25;               // tuna heals a moderate amount
export const FOOD_HEAL_CHICKEN = 35;            // chicken heals the most
export const FOOD_SPAWN_INTERVAL = 2000;   // ms between spawn attempts
export const FOOD_LIFETIME = 10000;        // ms before food disappears
export const FOOD_FADE_DURATION = 800;     // ms fade-out animation
export const MAX_ACTIVE_FOOD = 5;          // max simultaneous food items
export const FOOD_NEAR_PLAYER_CHANCE = 0.6; // 60% chance food spawns near the player's path

// --- Spawn Safety ---
export const MIN_SPAWN_DISTANCE_FROM_PLAYER = 2; // min Manhattan distance from player for food/coin spawns

// --- Fog of War ---
export const FOG_REVEAL_RADIUS = 3;        // tiles visible around the player
export const FOG_VISITED_ALPHA = 0.25;     // alpha for previously visited but not adjacent tiles

// --- Coins ---
export const COIN_SPAWN_INTERVAL = 5000;   // ms between coin spawn attempts
export const COIN_LIFETIME = 12000;        // ms before a coin disappears
export const COIN_FADE_DURATION = 800;     // ms fade-out animation
export const MAX_ACTIVE_COINS = 3;         // max simultaneous coins on the map
export const COIN_NEAR_PLAYER_CHANCE = 0.5; // 50% chance coin spawns near the player
export const SCORE_PER_COIN = 25;          // points awarded per coin
export const COINS_PER_EXTRA_LIFE = 10;    // every N coins = 1 extra life

// --- UI / HUD ---
export const BORDER_MARGIN = 20;              // px margin for the decorative border
export const BORDER_INNER = BORDER_MARGIN + 5; // px inner edge (where content starts)
export const HUD_HEIGHT = 33;              // px total height of the HUD bar (background + border)
export const HUD_PADDING = 12;
export const HUD_BAR_WIDTH = 120;
export const HUD_BAR_HEIGHT = 14;

// --- Scoring ---
export const SCORE_PER_FOOD = 50;              // points awarded per food collected
export const LEVEL_TIME_BONUS_BASE = 500;      // max time bonus per level
export const LEVEL_TIME_BONUS_DECAY = 0.015;   // exponential decay rate (per second)
export const SCORE_TO_COINS_RATE = 100;        // every N score points = 1 wallet coin on game over

// --- Audio ---
export const AUDIO_TITLE_BG = 'audio-title-bg';
export const AUDIO_COIN = 'audio-coin';
export const AUDIO_FOOD_FRUIT = 'audio-food-fruit';
export const AUDIO_FOOD_CHICKEN = 'audio-food-chicken';
export const AUDIO_FOOD_TUNA = 'audio-food-tuna';
export const AUDIO_BALL_MOVING = 'audio-ball-moving';
export const AUDIO_GAME_BG = 'audio-game-bg';
/** Total number of game background music tracks (game-music-bg-1.mp3 … 9.mp3). */
export const AUDIO_GAME_BG_COUNT = 9;
export const AUDIO_LEVEL_COMPLETE = 'audio-level-complete';
export const AUDIO_LEVEL_NEW = 'audio-level-new';
export const AUDIO_HEART_LOSS = 'audio-heart-loss';
export const AUDIO_WATER_WALK = 'audio-water-walk';
export const AUDIO_GAME_END = 'audio-game-end';
export const AUDIO_GAME_END_RESULTS = 'audio-game-end-results';
export const AUDIO_MENU_CLICK = 'audio-menu-click';
export const AUDIO_ESC_PRESSED = 'audio-esc-pressed';
export const AUDIO_MENU_SELECT = 'audio-menu-select';
export const AUDIO_RECOVER_LIFE = 'audio-recover-life';

// --- Player Movement Sound ---
export const BALL_SOUND_INTERVAL = 1500;  // ms between ball-moving sound repeats

// --- Water Hazard ---
export const WATER_MIN_LEVEL = 2;             // Water puddles appear from this level onward
export const WATER_PATCH_MIN_LENGTH = 2;      // Min tiles in a water patch
export const WATER_PATCH_MAX_LENGTH = 4;      // Max tiles in a water patch
export const WATER_MAX_PATCHES_BASE = 3;      // Base number of water patches at WATER_MIN_LEVEL
export const WATER_PATCHES_PER_LEVEL = 2;     // Additional patches per level above WATER_MIN_LEVEL
export const WATER_DRAIN_MULTIPLIER = 2;      // Health drain multiplier when walking on water
export const WATER_COVERAGE_CAP = 0.15;       // Max fraction of path cells that can be water
export const WATER_IDLE_DRAIN_INTERVAL = 1000; // ms between idle drain ticks while standing on water
export const WATER_IDLE_DRAIN_AMOUNT = 2;      // HP lost per idle drain tick on water (matches move drain)

// --- Idle Rest / Sleep ---
export const REST_IDLE_DELAY = 5000;          // ms idle before rest state begins
export const REST_HEAL_AMOUNT = 1;            // HP restored per heal tick (slow)
export const REST_HEAL_INTERVAL = 800;        // ms between heal ticks
export const REST_Z_MAX_VISIBLE = 3;          // max floating Z letters visible at once
export const REST_Z_SPAWN_INTERVAL = 900;     // ms between Z letter spawns
export const REST_SCORE_COST_PER_HEAL = 3;    // score points deducted per heal tick
                                               // (recovering 25 HP ≈ 75 pts, more than 1 food's 50 pts)

// --- Lava Hazard ---
export const LAVA_MIN_LEVEL = 3;              // Lava patches appear from this level onward
export const LAVA_PATCH_MIN_LENGTH = 3;       // Min tiles in a lava patch (longer than water)
export const LAVA_PATCH_MAX_LENGTH = 7;       // Max tiles in a lava patch (longer than water)
export const LAVA_MAX_PATCHES_BASE = 2;       // Base number of lava patches at LAVA_MIN_LEVEL
export const LAVA_PATCHES_PER_LEVEL = 1;      // Additional patches per level above LAVA_MIN_LEVEL
export const LAVA_DRAIN_MULTIPLIER = 4;       // Health drain multiplier when walking on lava
export const LAVA_COVERAGE_CAP = 0.12;        // Max fraction of path cells that can be lava
export const LAVA_IDLE_DRAIN_INTERVAL = 800;  // ms between idle drain ticks while standing on lava
export const LAVA_IDLE_DRAIN_AMOUNT = 4;      // HP lost per idle drain tick on lava (matches move drain)
export const AUDIO_LAVA_WALK = 'audio-lava-walk'; // Audio key for lava walk sound (optional)

// --- Poison Hazard ---
export const POISON_MIN_LEVEL = 4;              // Poison patches appear from this level onward
export const POISON_PATCH_MIN_LENGTH = 2;       // Min tiles in a poison patch
export const POISON_PATCH_MAX_LENGTH = 5;       // Max tiles in a poison patch
export const POISON_MAX_PATCHES_BASE = 2;       // Base number of poison patches at POISON_MIN_LEVEL
export const POISON_PATCHES_PER_LEVEL = 1;      // Additional patches per level above POISON_MIN_LEVEL
export const POISON_COVERAGE_CAP = 0.10;        // Max fraction of path cells that can be poison
export const POISON_DURATION = 10000;           // ms the poison status effect lasts (10 seconds)
export const POISON_DRAIN_INTERVAL = 1000;      // ms between passive drain ticks while poisoned
export const POISON_DRAIN_AMOUNT = 2;           // HP lost per poison drain tick
export const POISON_SPEED_MULTIPLIER = 2;       // Move duration multiplier while poisoned (half speed)

// --- Spike Hazard ---
export const SPIKE_MIN_LEVEL = 5;                // Spike patches appear from this level onward
export const SPIKE_PATCH_MIN_LENGTH = 2;          // Min tiles in a spike patch
export const SPIKE_PATCH_MAX_LENGTH = 4;          // Max tiles in a spike patch
export const SPIKE_MAX_PATCHES_BASE = 2;          // Base number of spike patches at SPIKE_MIN_LEVEL
export const SPIKE_PATCHES_PER_LEVEL = 1;         // Additional patches per level above SPIKE_MIN_LEVEL
export const SPIKE_COVERAGE_CAP = 0.08;           // Max fraction of path cells that can be spikes
export const SPIKE_WOUND_DURATION = 10000;        // ms the wounded status effect lasts (10 seconds)
export const SPIKE_DRAIN_INTERVAL = 1200;         // ms between passive drain ticks while wounded
export const SPIKE_DRAIN_AMOUNT = 1;              // HP lost per wounded drain tick
export const SPIKE_SPEED_MULTIPLIER = 1.6;        // Move duration multiplier while wounded (slower)
export const AUDIO_SPIKE_WALK = 'audio-spike-walk'; // Audio key for spike walk sound
export const AUDIO_POISON_WALK = 'audio-poison-walk'; // Audio key for poison walk sound

// --- Zoom-Out Map View ---
export const ZOOM_OUT_DURATION = 400;           // ms for smooth zoom-out transition
export const ZOOM_IN_DURATION = 300;            // ms for smooth zoom-in (return) transition
export const ZOOM_OUT_FOG_VISITED_ALPHA = 0.7;  // alpha for visited cells during zoom-out (clearer than normal)
export const AUDIO_ZOOM_OUT = 'audio-zoom-out'; // Audio key for zoom-out map view sound

// --- Keys & Chests ---
export const KEY_CHEST_MIN_LEVEL = 3;             // Keys and chests appear from this level onward
export const MAX_PLAYER_KEYS = 3;                 // Max keys the player can carry at once
export const KEY_SPAWN_INTERVAL = 12000;          // ms between key spawn attempts (infrequent)
export const KEY_NEAR_PLAYER_CHANCE = 0.4;        // 40% chance key spawns near the player
export const MAX_ACTIVE_KEYS = 1;                 // max simultaneous keys on the map at once

// Key tier distribution: metallic=60%, copper=30%, gold=10%
export const KEY_TIER_WEIGHTS = Object.freeze({ metallic: 0.60, copper: 0.30, gold: 0.10 });

// Chest placement — max per level
export const CHEST_MAX_METALLIC = 3;              // Max metallic chests per maze
export const CHEST_MAX_COPPER = 2;                // Max copper chests per maze
export const CHEST_MAX_GOLD = 1;                  // Max gold chests per maze

// Chest loot probability weights (per tier). Higher tiers = better loot.
// Categories: empty, coins, heart (extra life), star (full HP), agmuth
// Metallic chest loot weights
export const CHEST_LOOT_METALLIC = Object.freeze({
  empty: 0.10, coin_bag: 0.55, heart: 0.20, star: 0.14, agmuth: 0.01,
});
// Copper chest loot weights
export const CHEST_LOOT_COPPER = Object.freeze({
  empty: 0.05, coin_bag: 0.45, heart: 0.25, star: 0.20, agmuth: 0.05,
});
// Gold chest loot weights
export const CHEST_LOOT_GOLD = Object.freeze({
  empty: 0.02, coin_bag: 0.35, heart: 0.25, star: 0.25, agmuth: 0.13,
});

// Bag of Coins: fixed amount of coins awarded when collected
export const CHEST_COIN_BAG_AMOUNT = 10;

// Agmuth
export const AGMUTH_MAX_PER_GAME = 4;            // Max Agmuth collectible in a single playthrough

// Chest open effects
export const CHEST_OPEN_ZOOM_AMOUNT = 1.05;       // Subtle camera zoom on chest open
export const CHEST_OPEN_SHAKE_INTENSITY = 0.002;   // Subtle camera shake intensity
export const CHEST_OPEN_SHAKE_DURATION = 300;      // ms of camera shake on open
export const CHEST_OPEN_LIGHT_DURATION = 800;      // ms for the light burst effect
export const CHEST_GOLD_FLASH_DURATION = 400;      // ms for the gold chest screen flash

// Audio keys for keys & chests
export const AUDIO_KEY_COLLECT = 'audio-key-collect';
export const AUDIO_CHEST_OPEN = 'audio-chest-open';
export const AUDIO_CHEST_LOCKED = 'audio-chest-locked';
export const AUDIO_HEART_COLLECT = 'audio-heart-collect';
export const AUDIO_STAR_COLLECT = 'audio-star-collect';
export const AUDIO_AGMUTH_COLLECT = 'audio-agmuth-collect';
export const AUDIO_AGMUTH_USED = 'audio-agmuth-used';
export const AUDIO_COIN_BAG_COLLECT = 'audio-coin-bag-collect';

// --- Compass ---
export const COMPASS_SPAWN_CHANCE = 0.30;             // 30% chance the compass appears on a level
export const COMPASS_OSCILLATION_RANGE = 0.12;        // radians — max angular wobble from true heading
export const COMPASS_OSCILLATION_SPEED = 1500;        // ms per oscillation cycle (slow, realistic)
export const COMPASS_GLINT_INTERVAL_MIN = 4000;       // min ms between random glints
export const COMPASS_GLINT_INTERVAL_MAX = 9000;       // max ms between random glints
export const COMPASS_GLINT_DURATION = 600;            // ms for a single glint flash
export const COMPASS_DISPLAY_SIZE = 38;               // px — compass HUD widget diameter
export const COMPASS_NEEDLE_COLOR = 0xffffff;         // Needle color (white)
export const COMPASS_RING_COLOR = 0x888888;           // Ring color (gray)
export const COMPASS_DEFAULT_ALPHA = 0.92;            // Default idle opacity (more visible)
export const COMPASS_PROXIMITY_ALPHA = 0.35;          // Opacity when player is near the widget
export const COMPASS_PROXIMITY_RADIUS = 100;          // px — screen distance to trigger fade
export const COMPASS_EXIT_FULGOR_DISTANCE = 3;        // Grid cells from exit to trigger fulgor
export const COMPASS_EXIT_FULGOR_INTERVAL = 400;      // ms between fulgor bursts when near exit
export const AUDIO_COMPASS_COLLECT = 'audio-compass-collect'; // Audio key for compass pickup

// --- Agmuth Ghost Mode ---
export const AGMUTH_GHOST_ALPHA = 0.45;            // Player sphere alpha during ghost mode
export const AGMUTH_GHOST_GLOW_COLOR = 0xffd700;   // Gold glow color during ghost mode
export const AGMUTH_GHOST_GLOW_ALPHA = 0.6;        // Glow particle alpha
export const AGMUTH_GHOST_PARTICLE_FREQ = 40;       // ms between glow particle emissions

// --- Threia's Blessing ---
export const THREIA_TOTEM_SPAWN_CHANCE = 0.35;        // 35% chance the totem appears on a level
export const THREIA_BLESSING_CHANCE_BASE = 0.35;      // 35% blessing success without totem
export const THREIA_BLESSING_CHANCE_TOTEM = 1.0;      // 100% success with totem
export const THREIA_LIFE_RESTORE_CHANCE_BASE = 0.40;  // 40% life restore without totem
export const THREIA_LIFE_RESTORE_CHANCE_TOTEM = 1.0;  // 100% life restore with totem

// Prayer mechanic timing
export const THREIA_PRAYER_PRESS_INTERVAL = 400;      // Max ms between H presses to count as rapid
export const THREIA_PRAYER_GLOW_PHASES = 3;           // Number of glow intensity phases
export const THREIA_PRAYER_PHASE_PRESSES = 4;         // H presses per glow phase
export const THREIA_PRAYER_FINAL_PRESSES = 6;         // Additional presses after max glow for blessing
export const THREIA_GLOW_BASE_ALPHA = 0.3;            // Starting glow alpha
export const THREIA_GLOW_MAX_ALPHA = 0.9;             // Max glow alpha at phase 3
export const THREIA_BLESSING_FLASH_DURATION = 1200;   // ms for the celestial light sequence
export const THREIA_BLESSING_TEXT_DURATION = 2500;     // ms to show "Threia bless you!" text
export const THREIA_TOTEM_CONSUME_DURATION = 600;     // ms for totem HUD disappear animation

// Threia Audio Keys
export const AUDIO_THREIA_TOTEM_COLLECT = 'audio-threia-totem-collect';
export const AUDIO_THREIA_PRAYER_HUM = 'audio-threia-prayer-hum';
export const AUDIO_THREIA_BLESSING = 'audio-threia-blessing';
export const AUDIO_THREIA_FAIL = 'audio-threia-fail';

// Threia Colors
export const COLOR_THREIA_GLOW = 0xfff8dc;            // Warm celestial white-gold
export const COLOR_THREIA_GOLD = 0xffd700;            // Gold for blessing effects

// --- Shop System ---
// Persistent wallet (coins survive game over, used as shop currency)
export const SHOP_WALLET_KEY = 'lost-in-labyrinth-wallet';
export const SHOP_ITEMS_KEY = 'lost-in-labyrinth-shop-items';
export const SHOP_DURABILITY_KEY = 'lost-in-labyrinth-durability';

// Shop item identifiers
export const SHOP_ITEM_THREIAS_GUIDE = 'threias-guide';
export const SHOP_ITEM_HEART_POTION = 'heart-potion';
export const SHOP_ITEM_TOTEM_VORACIOUS = 'totem-voracious';
export const SHOP_ITEM_AGMUTH_PENDANT = 'agmuth-pendant';
export const SHOP_ITEM_LAVA_RING = 'lava-ring';
export const SHOP_ITEM_POISON_RING = 'poison-ring';
export const SHOP_ITEM_WATER_RING = 'water-ring';
export const SHOP_ITEM_KEY_PENDANT = 'key-pendant';
export const SHOP_ITEM_HEART_PENDANT = 'heart-pendant';
export const SHOP_ITEM_ELEMENTAL_RING = 'elemental-ring';
export const SHOP_ITEM_TEAR_OF_THREIA = 'tear-of-threia';
export const SHOP_ITEM_KEY_METALLIC = 'key-metallic';
export const SHOP_ITEM_KEY_COPPER = 'key-copper';
export const SHOP_ITEM_KEY_GOLD = 'key-gold';

// Shop item prices (in persistent coins)
export const SHOP_PRICE_THREIAS_GUIDE = 500;
export const SHOP_PRICE_HEART_POTION = 100;
export const SHOP_PRICE_TOTEM_VORACIOUS = 450;
export const SHOP_PRICE_AGMUTH_PENDANT = 650;
export const SHOP_PRICE_LAVA_RING = 250;
export const SHOP_PRICE_POISON_RING = 250;
export const SHOP_PRICE_WATER_RING = 200;
export const SHOP_PRICE_KEY_PENDANT = 400;
export const SHOP_PRICE_HEART_PENDANT = 500;
export const SHOP_PRICE_ELEMENTAL_RING = 600;
export const SHOP_PRICE_TEAR_OF_THREIA = 800;
export const SHOP_PRICE_KEY_METALLIC = 15;
export const SHOP_PRICE_KEY_COPPER = 30;
export const SHOP_PRICE_KEY_GOLD = 60;

// Shop item max purchase quantities
export const SHOP_MAX_THREIAS_GUIDE = 1;
export const SHOP_MAX_HEART_POTION = 3;         // max purchasable per session
export const SHOP_MAX_TOTEM_VORACIOUS = 1;
export const SHOP_MAX_AGMUTH_PENDANT = 1;
export const SHOP_MAX_LAVA_RING = 1;
export const SHOP_MAX_POISON_RING = 1;
export const SHOP_MAX_WATER_RING = 1;
export const SHOP_MAX_KEY_PENDANT = 1;
export const SHOP_MAX_HEART_PENDANT = 1;
export const SHOP_MAX_ELEMENTAL_RING = 1;
export const SHOP_MAX_TEAR_OF_THREIA = 1;
export const SHOP_MAX_KEY_METALLIC = 1;
export const SHOP_MAX_KEY_COPPER = 1;
export const SHOP_MAX_KEY_GOLD = 1;

// --- Shop Item Effect Values ---

// Threia's Guide: chance per level to reveal entire maze (remove fog)
export const THREIAS_GUIDE_REVEAL_CHANCE = 0.40;   // 40% per level

// Heart Potion: max active potions the player can have
export const HEART_POTION_MAX_ACTIVE = 3;

// Totem of the Voracious: food attraction radius (in grid tiles)
export const TOTEM_VORACIOUS_ATTRACT_RADIUS = 2;   // logical tiles
export const TOTEM_VORACIOUS_ATTRACT_SPEED = 120;   // pixels/sec food moves toward player

// Agmuth Pendant: bonus probability for agmuth drops from copper/gold chests
export const AGMUTH_PENDANT_BONUS = 0.30;          // +30% to agmuth weight

// Key Pendant: bonus to key spawn chance
export const KEY_PENDANT_SPAWN_BONUS = 0.20;        // +20% added to spawn attempts

// Heart Pendant: reduces base health drain per move
export const HEART_PENDANT_DRAIN_REDUCTION = 0.20;  // 20% less drain per step

// --- Item Durability (charges — item breaks when depleted) ---
// Durability-based items persist across game overs but degrade with use.
// When charges reach 0 the item is destroyed and can be repurchased.
export const DURABILITY_THREIAS_GUIDE = 5;            // charges (1 per successful maze reveal)
export const DURABILITY_WATER_RING = 80;             // charges (1 per protected water step/idle tick)
export const DURABILITY_LAVA_RING = 50;              // charges (1 per protected lava step/idle tick)
export const DURABILITY_POISON_RING = 60;            // charges (1 per protected poison step)
export const DURABILITY_ELEMENTAL_RING = 100;        // charges (shared pool for any hazard protection)
export const DURABILITY_TOTEM_VORACIOUS = 120;       // charges (1 per food item attracted)
export const DURABILITY_HEART_PENDANT = 200;         // charges (1 per step with drain reduction)

// Ring glow effect
export const RING_GLOW_PULSE_SPEED = 800;           // ms per glow pulse cycle
export const RING_GLOW_MIN_ALPHA = 0.3;
export const RING_GLOW_MAX_ALPHA = 0.8;

// Shop Audio Keys
export const AUDIO_SHOP_BUY = 'audio-shop-buy';
export const AUDIO_SHOP_DENY = 'audio-shop-deny';
export const AUDIO_HEART_POTION_USE = 'audio-heart-potion-use';
export const AUDIO_ITEM_BREAK = 'audio-shop-deny';   // reuse deny sound for item breaking
export const AUDIO_TEAR_OF_THREIA_USE = 'audio-tear-of-threia-use';

// Shop Colors
export const COLOR_POTION_GREEN = 0x00cc66;          // Heart Potion green tint
export const COLOR_LAVA_RING = 0xff4400;             // Lava Ring red-orange
export const COLOR_POISON_RING = 0x00cc44;           // Poison Ring green
export const COLOR_WATER_RING = 0x4488ff;            // Water Ring blue
export const COLOR_PENDANT_GEM = 0xaa44ff;           // Agmuth Pendant violet gem
export const COLOR_KEY_PENDANT = 0xdddddd;           // Key Pendant silver
export const COLOR_HEART_PENDANT = 0xcc3344;          // Heart Pendant reddish heart
export const COLOR_ELEMENTAL_RING = 0x8899aa;          // Elemental Ring muted silver-blue
export const COLOR_VORACIOUS_VIOLET = 0x9933cc;      // Totem of the Voracious violet
export const COLOR_GUIDE_PARCHMENT = 0xf5e6c8;       // Threia's Guide parchment

// Tear of Threia colors
export const COLOR_TEAR_THREIA_BLUE = 0x88ccff;        // Teardrop outer — light celestial blue
export const COLOR_TEAR_THREIA_GOLD = 0xffd700;        // Teardrop inner — golden glow

// --- Hammer of Threia ---
export const HAMMER_SPAWN_CHANCE = 0.25;               // 25% chance the hammer appears on a level
export const HAMMER_PICKUP_CHARGES = 3;                // Charges when found as floor pickup
export const HAMMER_BREAK_PRESSES = 5;                 // B-key presses required to break a wall
export const HAMMER_PRESS_INTERVAL = 500;              // Max ms between B presses to count as combo
export const HAMMER_WALL_REGEN_TIME = 45000;           // ms before a broken wall regenerates (45s)
export const HAMMER_GLOW_RADIUS = 2;                   // Tiles radius for break glow flash
export const HAMMER_GLOW_FLASH_DURATION = 800;         // ms for the break glow flash
export const HAMMER_VIBRATE_INTENSITY = 1.5;           // Pixel offset for wall vibration per hit

// Hammer of Threia Audio Keys
export const AUDIO_HAMMER_COLLECT = 'audio-hammer-collect';
export const AUDIO_HAMMER_HIT = 'audio-hammer-hit';
export const AUDIO_HAMMER_BREAK = 'audio-hammer-break';
export const AUDIO_WALL_REGEN = 'audio-wall-regen';

// Hammer of Threia Colors
export const COLOR_HAMMER_BLUE = 0x88ccff;             // Celestial blue (matching Threia line)
export const COLOR_HAMMER_GOLD = 0xffd700;             // Gold accents (matching Threia line)

// Shop: Hammer of Threia
export const SHOP_ITEM_HAMMER_OF_THREIA = 'hammer-of-threia';
export const SHOP_PRICE_HAMMER_OF_THREIA = 550;
export const SHOP_MAX_HAMMER_OF_THREIA = 1;
export const DURABILITY_HAMMER_OF_THREIA = 10;         // Shop version: 10 charges

// --- Configuration / Settings ---
// Volume steps (9 positions: 0% / 12.5% / 25% / 37.5% / 50% / 62.5% / 75% / 87.5% / 100%)
export const VOLUME_STEPS = Object.freeze([0, 0.125, 0.25, 0.375, 0.5, 0.625, 0.75, 0.875, 1.0]);
export const DEFAULT_MUSIC_VOLUME_STEP = 4;     // 50% — index into VOLUME_STEPS
export const DEFAULT_SFX_VOLUME_STEP = 6;       // 75%
export const DEFAULT_MASTER_MUTE = false;

// Difficulty levels
export const DIFFICULTY_EASY = 'easy';
export const DIFFICULTY_NORMAL = 'normal';
export const DIFFICULTY_HARD = 'hard';
export const DEFAULT_DIFFICULTY = DIFFICULTY_NORMAL;

// Difficulty drain multipliers (applied to ALL health drains)
// Easy   = relaxed exploration, minimal energy pressure
// Normal = challenging but fair, energy management matters
// Hard   = demanding, every step counts (previous "Normal")
export const DIFFICULTY_DRAIN_MULTIPLIER = Object.freeze({
  [DIFFICULTY_EASY]: 0.4,
  [DIFFICULTY_NORMAL]: 0.75,
  [DIFFICULTY_HARD]: 1.0,
});

// Audio key for config UI
export const AUDIO_CONFIG_CHANGE = 'audio-menu-select';  // reuse existing menu sound

// --- Explorer Mode ---
export const EXPLORER_FREE_CONTINUES = 2;            // Free continues before coins are required
export const EXPLORER_SCORE_PENALTY = 0.35;           // 35% score lost per continue (always applied)
export const EXPLORER_COIN_PENALTY = 0.25;            // 25% of total coins (run + wallet) per paid continue
export const EXPLORER_MIN_COIN_COST = 6;              // Minimum coin cost for a paid continue

// --- Bat System ---
export const BAT_SPAWN_CHANCE = 0.04;               // 4% chance per new-cell reveal
export const BAT_DOUBLE_SPAWN_CHANCE = 0.015;        // 1.5% additional chance for a second bat
export const BAT_MAX_SPAWNS_PER_LEVEL = 3;           // Max spawn events per maze level
export const BAT_MIN_LEVEL = 2;                      // Bats appear from level 2+
export const BAT_SIZE = 14;                          // Bat sprite size in pixels
export const BAT_FLIGHT_SPEED = 90;                  // Base flight speed in pixels/sec
export const BAT_ATTACK_SPEED = 160;                 // Attack dive speed in pixels/sec
export const BAT_FLEE_SPEED = 130;                   // Flee speed after hit/repel in pixels/sec
export const BAT_ATTACK_DAMAGE_MIN = 8;              // Minimum HP damage on hit
export const BAT_ATTACK_DAMAGE_MAX = 12;             // Maximum HP damage on hit
export const BAT_MIN_DISTANCE_TILES = 2;             // Min distance from player during flight (tiles)
export const BAT_SCREECH_INTERVAL_MIN = 1500;        // Min ms between screeches
export const BAT_SCREECH_INTERVAL_MAX = 4000;        // Max ms between screeches
export const BAT_SCREECH_MAX = 3;                    // Max screeches per bat lifetime
export const BAT_ERRATIC_AMPLITUDE = 18;             // Sinusoidal flight amplitude in pixels
export const BAT_ERRATIC_FREQUENCY = 0.004;          // Sinusoidal flight frequency
export const BAT_WING_FLAP_SPEED = 180;              // ms between wing frame swaps
export const BAT_DESPAWN_ALPHA_DURATION = 600;        // Fade-out duration in ms when leaving visible area
export const BAT_REPEL_FLASH_DURATION = 400;          // Threia totem repel flash duration in ms

// Bat audio keys
export const AUDIO_BAT_SCREECH = 'audio-bat-screech';
export const AUDIO_BAT_HIT = 'audio-bat-hit';
export const AUDIO_BAT_REPEL = 'audio-bat-repel';

// Bat eye color (red)
export const COLOR_BAT_EYES = 0xff0000;

// --- Colors (monochrome palette) ---
export const COLOR_WHITE = 0xffffff;
export const COLOR_BLACK = 0x000000;
export const COLOR_GRAY = 0x888888;
export const COLOR_DARK_GRAY = 0x444444;

// --- Wall Themes ---
// Subtle accent tones for wall material details (mortar, grain, seams).
// These stay within a narrow grayscale range to preserve the newspaper aesthetic.
export const COLOR_WALL_MORTAR = 0xbbbbbb;         // Brick mortar lines
export const COLOR_WALL_BRICK_FACE = 0xe8e8e8;     // Brick face (slightly off-white)
export const COLOR_WALL_STONE_LIGHT = 0xeeeeee;    // Light stone block
export const COLOR_WALL_STONE_MID = 0xd8d8d8;      // Mid-tone stone block
export const COLOR_WALL_STONE_SEAM = 0xaaaaaa;     // Stone seam / crack line
export const COLOR_WALL_WOOD_PLANK = 0xf0e8dc;     // Warm off-white wood plank
export const COLOR_WALL_WOOD_GRAIN = 0xc8b8a0;     // Subtle warm grain line
export const COLOR_WALL_WOOD_GAP = 0xa09080;        // Plank gap / knot detail
// Metal / Iron
export const COLOR_WALL_METAL_PLATE = 0xcccccc;     // Steel plate surface
export const COLOR_WALL_METAL_DARK = 0x999999;       // Darker plate edge / bevel
export const COLOR_WALL_METAL_RIVET = 0xe0e0e0;     // Bright rivet highlight
export const COLOR_WALL_METAL_SEAM = 0x777777;       // Seam between plates
// Lattice / Celosía
export const COLOR_WALL_LATTICE_BAR = 0xdddddd;     // Lattice bar (light)
export const COLOR_WALL_LATTICE_BG = 0x333333;       // Void behind lattice (dark)
export const COLOR_WALL_LATTICE_HIGHLIGHT = 0xf0f0f0; // Bar highlight
// Ruins / Agrietado
export const COLOR_WALL_RUINS_BLOCK = 0xd0d0d0;     // Weathered block surface
export const COLOR_WALL_RUINS_CRACK = 0x666666;       // Dark crack lines
export const COLOR_WALL_RUINS_SHADOW = 0x999999;     // Missing-chunk shadow
// Marble / Mármol
export const COLOR_WALL_MARBLE_BASE = 0xf0f0f0;     // Polished marble surface
export const COLOR_WALL_MARBLE_VEIN = 0xbbbbbb;     // Gray vein line
export const COLOR_WALL_MARBLE_VEIN2 = 0xcccccc;    // Secondary vein (lighter)
// Adobe / Tierra
export const COLOR_WALL_ADOBE_BASE = 0xe0d4c0;      // Dried clay / mud color
export const COLOR_WALL_ADOBE_CRACK = 0xa08860;      // Crack / texture lines
export const COLOR_WALL_ADOBE_SHADOW = 0xc0b090;     // Shadow patches
/** Number of wall themes. Levels cycle through 0..WALL_THEME_COUNT-1. */
export const WALL_THEME_COUNT = 9;

// --- Key & Chest Colors ---
export const COLOR_METALLIC = 0xaaaaaa;           // Silver/steel gray
export const COLOR_METALLIC_DARK = 0x777777;      // Darker metallic
export const COLOR_COPPER = 0xb87333;             // Copper/bronze
export const COLOR_COPPER_DARK = 0x8b5a2b;        // Darker copper
export const COLOR_GOLD = 0xffd700;               // Gold
export const COLOR_GOLD_DARK = 0xdaa520;          // Darker gold

// --- Narrator System ---
// Monk — wise elder from the Order of Threia, appears periodically
export const NARRATOR_MONK_FIRST_LEVEL = 2;           // Earliest level the monk can appear
export const NARRATOR_MONK_MIN_INTERVAL = 2;          // Min levels between monk appearances
export const NARRATOR_MONK_MAX_INTERVAL = 3;          // Max levels between monk appearances
// Vorath — fallen guardian, menacing antagonist, appears less frequently
export const NARRATOR_VORATH_FIRST_LEVEL = 4;         // Earliest level Vorath can appear
export const NARRATOR_VORATH_MIN_INTERVAL = 4;        // Min levels between Vorath appearances
export const NARRATOR_VORATH_MAX_INTERVAL = 5;        // Max levels between Vorath appearances

// Narrator modal visuals
export const COLOR_NARRATOR_MONK = 0xfff8dc;          // Warm celestial white (cornsilk) for monk text
export const COLOR_NARRATOR_MONK_NAME = 0xffd700;     // Gold for monk name
export const COLOR_NARRATOR_VORATH = 0xff4444;         // Blood-red for Vorath text
export const COLOR_NARRATOR_VORATH_NAME = 0xcc0000;    // Dark red for Vorath name
export const COLOR_NARRATOR_VORATH_BG = 0x1a0000;      // Near-black red tint for Vorath overlay

// Narrator audio
export const AUDIO_NARRATOR_MONK = 'audio-narrator-monk';
export const AUDIO_NARRATOR_VORATH = 'audio-narrator-vorath';

// --- Vorath's Treasure Room ---
// Golden exit — bonus portal to Vorath's coin hoard
export const TREASURE_ROOM_LEVEL_INTERVAL = 3;        // Golden exit can appear every N levels (3, 6, 9…)
export const TREASURE_ROOM_SPAWN_CHANCE = 0.40;        // 40% probability when interval hit
export const TREASURE_ROOM_FIRST_LEVEL = 3;            // Earliest level golden exit can appear
export const TREASURE_ROOM_TIMER_SECONDS = 45;         // Countdown duration inside treasure room
export const TREASURE_ROOM_WARNING_SECONDS = 10;       // Timer starts flashing when this many seconds remain
export const TREASURE_ROOM_COIN_COUNT = 150;           // Individual coins scattered in the bonus maze
export const TREASURE_ROOM_COIN_BAG_COUNT = 4;         // Coin bags (10 coins each) in the bonus maze
export const TREASURE_ROOM_MAZE_COLS = 15;             // Bonus maze dimensions (same as level 1)
export const TREASURE_ROOM_MAZE_ROWS = 13;
export const TREASURE_ROOM_MUSIC_COUNT = 3;            // Number of available bonus room music tracks

// Golden exit visuals
export const COLOR_GOLDEN_EXIT = 0xffd700;             // Gold color for the special exit
export const COLOR_GOLDEN_EXIT_DARK = 0xb8860b;        // Dark gold for glow edge
export const GOLDEN_EXIT_GLOW_PULSE_SPEED = 1200;      // ms per glow pulse cycle
export const GOLDEN_EXIT_SPENT_ALPHA = 0.25;           // Alpha for deteriorated (spent) golden exit

// Treasure room audio
export const AUDIO_TREASURE_BG = 'audio-treasure-bg';  // Prefix for bonus room music (1-3)
export const AUDIO_TREASURE_TICK = 'audio-treasure-tick'; // Timer tick sound (last 10 seconds)
export const AUDIO_TREASURE_ENTER = 'audio-treasure-enter'; // Sound when entering golden exit
export const AUDIO_TREASURE_EXIT = 'audio-treasure-exit'; // Sound when expelled from treasure room

// --- Manuscript System ---
export const MANUSCRIPT_SPAWN_CHANCE = 0.40;          // 40% chance a manuscript spawns on a level
export const AUDIO_MANUSCRIPT_COLLECT = 'audio-manuscript-collect';
export const AUDIO_MANUSCRIPT_OPEN = 'audio-manuscript-open';
export const AUDIO_LIBRARY_BG = 'audio-library-bg';
export const COLOR_MANUSCRIPT_PARCHMENT = 0xf0dcc0;  // Aged parchment color (slightly distinct from Guide)

// --- Final Battle (Vorath's Demise) ---
// Celestial exit that appears when player has all 4 Agmuth relics
export const FINAL_CELESTIAL_EXIT_MATERIALIZE_DURATION = 2000; // ms for the exit to materialize
export const FINAL_VORATH_MAZE_COLS = 15;              // Vorath's arena maze columns (same as level 1)
export const FINAL_VORATH_MAZE_ROWS = 13;              // Vorath's arena maze rows
export const FINAL_VORATH_PRISON_SIZE = 5;             // 5×5 logical cells for Vorath's prison in center
export const FINAL_VORATH_SPHERE_DAMAGE = 100;         // HP per dark sphere hit (= 1 full heart)
export const FINAL_VORATH_SPHERE_INITIAL_INTERVAL = 4000; // ms between sphere launches (initial)
export const FINAL_VORATH_SPHERE_MIN_INTERVAL = 1500;  // ms between sphere launches (fastest)
export const FINAL_VORATH_SPHERE_ACCEL = 0.92;         // Multiplier per launch to accelerate interval
export const FINAL_VORATH_SPHERE_SPEED = 140;          // px/s sphere travel speed
export const FINAL_VORATH_SPHERE_SIZE = 16;            // Sphere sprite size in pixels
export const FINAL_VORATH_PULSE_SPEED = 800;           // ms per Vorath pulse cycle
export const FINAL_ALTAR_BEAM_INTERVAL = 1000;         // ms between each altar firing its beam
export const FINAL_ALTAR_BEAM_DURATION = 2000;         // ms the beams hit Vorath before destruction
export const FINAL_VORATH_DESTROY_DURATION = 2500;     // ms for Vorath destruction explosion
export const FINAL_VORATH_SIZE = 192;                  // Vorath mass sprite size in pixels (enormous deformed mass)
export const FINAL_ALTAR_SIZE = 48;                    // Altar sprite size in pixels (2×2 tiles)
export const FINAL_PLAYER_PROXIMITY_TRIGGER = 3;       // Tiles from Vorath to trigger destruction
export const FINAL_VORATH_CONTACT_DAMAGE = 100;        // HP when player touches Vorath (= 1 full heart)
export const FINAL_VORATH_CONTACT_RADIUS = 80;         // px distance from Vorath center to trigger contact damage
export const FINAL_VORATH_REPEL_DURATION = 200;        // ms for repulsion tween after Vorath contact
export const FINAL_VORATH_INVULN_DURATION = 1000;      // ms invulnerability window after Vorath/sphere hit
export const FINAL_VORATH_HOMING_SPHERE_SPEED = 110;   // px/s homing sphere speed (slower than normal)
export const FINAL_VORATH_HOMING_GRACE_MS = 600;       // ms of straight-line flight before homing kicks in

// Final battle colors
export const COLOR_CELESTIAL_EXIT = 0xffffff;          // White celestial exit
export const COLOR_CELESTIAL_GOLD = 0xffd700;          // Golden celestial accent
export const COLOR_CELESTIAL_ORANGE = 0xffaa44;        // Orange celestial accent
export const COLOR_VORATH_DARK = 0x1a0808;             // Vorath mass dark color
export const COLOR_VORATH_RED = 0x880000;              // Vorath blood-red edge
export const COLOR_VORATH_ORANGE = 0xcc4400;           // Vorath orange flashes
export const COLOR_ALTAR_GOLD = 0xffd700;              // Altar golden glow
export const COLOR_ALTAR_CELESTIAL = 0xfff8dc;         // Altar celestial white
export const COLOR_DARK_SPHERE = 0x110000;             // Dark sphere core color
export const COLOR_DARK_SPHERE_RIM = 0xcc2200;         // Dark sphere red rim
export const COLOR_THREIA_MODAL_BG = 0x0a0a1a;         // Threia modal deep blue-black
export const COLOR_THREIA_MODAL_BORDER = 0xffd700;     // Threia modal golden border
export const COLOR_THREIA_MODAL_TEXT = 0xfff8dc;        // Threia modal cornsilk text
export const COLOR_THREIA_MODAL_NAME = 0xffd700;        // Threia name golden

// Final battle audio
export const AUDIO_CELESTIAL_MATERIALIZE = 'audio-celestial-materialize';  // Celestial exit materialization
export const AUDIO_CELESTIAL_HUM = 'audio-celestial-hum';                 // Celestial exit ambient hum
export const AUDIO_FINAL_ENERGY = 'audio-final-energy';                   // Energy buildup sound
export const AUDIO_THREIA_VOICE = 'audio-threia-voice';                   // Threia speaks
export const AUDIO_THREIA_MUSIC = 'audio-threia-music';                   // Threia background music
export const AUDIO_VORATH_MACABRE = 'audio-vorath-macabre';               // Vorath macabre ambient
export const AUDIO_VORATH_ARENA_MUSIC = 'audio-vorath-arena-music';       // Vorath arena battle music
export const AUDIO_VORATH_SPHERE_LAUNCH = 'audio-vorath-sphere-launch';   // Dark sphere launch
export const AUDIO_VORATH_SPHERE_HIT = 'audio-vorath-sphere-hit';         // Dark sphere hits player
export const AUDIO_VORATH_SPHERE_WALL = 'audio-vorath-sphere-wall';       // Dark sphere breaks wall
export const AUDIO_ALTAR_ACTIVATE = 'audio-altar-activate';               // Altar activation sound
export const AUDIO_ALTAR_BEAM = 'audio-altar-beam';                       // Altar beam firing
export const AUDIO_VORATH_DESTROY = 'audio-vorath-destroy';               // Vorath destruction
export const AUDIO_FINAL_CELESTIAL = 'audio-final-celestial';             // Celestial sound after Vorath dies
export const AUDIO_FINAL_MUSIC = 'audio-final-music';                     // THE END background music
export const AUDIO_FINAL_TRANSITION = 'audio-final-transition';           // Transition to white/dark

// ═══════════════════════════════════════════════════════════════════
// Level Events
// ═══════════════════════════════════════════════════════════════════
export const LEVEL_EVENT_MIN_LEVEL = 3;                  // Level events can trigger from level 3+
export const LEVEL_EVENT_CHANCE = 0.20;                  // 20% chance per eligible level
export const LEVEL_EVENT_FOG_RADIUS_BONUS = 2;           // "Threia's Light" — extra fog reveal radius (+2 → total 5)
export const LEVEL_EVENT_FOOD_LIFETIME_MULT = 0.5;       // "Vorath's Hunger" — food lifetime halved
export const LEVEL_EVENT_COIN_MULTIPLIER = 3;            // "Gold Rush" — coin spawn multiplier
export const LEVEL_EVENT_FOG_RE_DARKEN_DELAY = 8000;     // "The Labyrinth Remembers" — ms before visited cells re-darken
export const LEVEL_EVENT_FOG_RE_DARKEN_RATE = 500;       // "The Labyrinth Remembers" — ms between fog re-darken ticks

// Level event display
export const COLOR_LEVEL_EVENT = 0xffd700;               // Gold color for level event announcement text
export const LEVEL_EVENT_ANNOUNCE_DURATION = 3000;        // ms to show level event subtitle in announcement

// ═══════════════════════════════════════════════════════════════════
// Achievement System
// ═══════════════════════════════════════════════════════════════════
export const ACHIEVEMENTS_STORAGE_KEY = 'lost-in-labyrinth-achievements';

// Achievement thresholds
export const ACHIEVEMENT_EXPLORER_FOG_PCT = 100;         // Explore 100% of a maze
export const ACHIEVEMENT_GHOST_USES = 3;                 // Use Agmuth ghost mode 3 times in a single run
export const ACHIEVEMENT_PRAY_BLESSINGS = 5;             // Receive 5 Threia blessings total (lifetime)
export const ACHIEVEMENT_RICH_WALLET = 500;              // Have 500+ coins in wallet
export const ACHIEVEMENT_SURVIVOR_LEVEL = 10;            // Reach level 10 on Hard difficulty
export const ACHIEVEMENT_SCHOLAR_MANUSCRIPTS = 30;       // Collect all 30 manuscripts
export const ACHIEVEMENT_HOARDER_COINS = 100;            // Collect 100 coins in a single run
export const ACHIEVEMENT_DESTROYER_WALLS = 10;           // Break 10 walls in a single run
export const ACHIEVEMENT_TREASURE_HUNTER_CHESTS = 15;    // Open 15 chests in a single run
export const ACHIEVEMENT_VORATH_DEFEATED = 1;            // Defeat Vorath in the final battle

// Achievement display colors
export const COLOR_ACHIEVEMENT_GOLD = 0xffd700;          // Gold for unlocked achievements
export const COLOR_ACHIEVEMENT_LOCKED = 0x555555;        // Gray for locked achievements
export const COLOR_ACHIEVEMENT_NEW = 0x44ff44;           // Green flash for newly unlocked

// ═══════════════════════════════════════════════════════════════════
// Records System (Best Games History)
// ═══════════════════════════════════════════════════════════════════
export const RECORDS_STORAGE_KEY = 'lost-in-labyrinth-records';
export const RECORDS_MAX_ENTRIES = 5;                    // Top 5 best games

// Records display colors
export const COLOR_RECORDS_GOLD = 0xffd700;              // Gold for #1 record
export const COLOR_RECORDS_SILVER = 0xc0c0c0;            // Silver for #2 record
export const COLOR_RECORDS_BRONZE = 0xcd7f32;            // Bronze for #3 record

// ═══════════════════════════════════════════════════════════════════
// Ambient Sounds (placeholder — MP3 files not yet provided)
// ═══════════════════════════════════════════════════════════════════
export const AUDIO_AMBIENT_WATER_DRIP = 'audio-ambient-water-drip';
export const AUDIO_AMBIENT_LAVA_CRACKLE = 'audio-ambient-lava-crackle';
export const AUDIO_AMBIENT_VORATH_WHISPER = 'audio-ambient-vorath-whisper';
export const AUDIO_AMBIENT_CREAK_1 = 'audio-ambient-creak-1';
export const AUDIO_AMBIENT_CREAK_2 = 'audio-ambient-creak-2';
export const AUDIO_AMBIENT_WIND = 'audio-ambient-wind';

// Ambient sound tuning
export const AMBIENT_PROXIMITY_RADIUS = 4;               // Tiles radius for proximity-based ambient sounds
export const AMBIENT_WATER_INTERVAL_MIN = 6000;           // Min ms between water drip sounds
export const AMBIENT_WATER_INTERVAL_MAX = 14000;          // Max ms between water drip sounds
export const AMBIENT_LAVA_INTERVAL_MIN = 5000;            // Min ms between lava crackle sounds
export const AMBIENT_LAVA_INTERVAL_MAX = 12000;           // Max ms between lava crackle sounds
export const AMBIENT_VORATH_INTERVAL_MIN = 20000;         // Min ms between Vorath whispers
export const AMBIENT_VORATH_INTERVAL_MAX = 45000;         // Max ms between Vorath whispers
export const AMBIENT_VORATH_MIN_LEVEL = 5;                // Vorath whispers start from level 5+
export const AMBIENT_CREAK_INTERVAL_MIN = 8000;           // Min ms between random creaks
export const AMBIENT_CREAK_INTERVAL_MAX = 25000;          // Max ms between random creaks
export const AMBIENT_WIND_INTERVAL_MIN = 15000;           // Min ms between wind gusts
export const AMBIENT_WIND_INTERVAL_MAX = 40000;           // Max ms between wind gusts
export const AMBIENT_BASE_VOLUME = 0.3;                   // Base volume for ambient sounds (before SFX scaling)

// ═══════════════════════════════════════════════════════════════════
// Moving Walls (level 8+)
// ═══════════════════════════════════════════════════════════════════
export const MOVING_WALL_MIN_LEVEL = 8;                    // Moving walls appear from level 8+
export const MOVING_WALL_CYCLE_MIN = 30000;                // Min ms between wall changes (30s)
export const MOVING_WALL_CYCLE_MAX = 50000;                // Max ms between wall changes (50s)
export const MOVING_WALL_WARNING_TIME = 2000;              // ms warning sound before change
export const MOVING_WALL_TRANSITION_DURATION = 600;        // ms for wall appear/disappear animation
export const MOVING_WALL_COUNT_BASE = 3;                   // Base number of wall pairs at level 8
export const MOVING_WALL_COUNT_PER_LEVEL = 1;              // Additional pairs per level above 8
export const MOVING_WALL_COUNT_MAX = 8;                    // Max wall pairs per level
export const MOVING_WALL_PLAYER_RETRY_DELAY = 2000;        // ms retry if player blocks wall placement

// Moving wall audio
export const AUDIO_WALL_SHIFT_WARNING = 'audio-wall-shift-warning';     // Deep rumble warning (placeholder)
export const AUDIO_WALL_SHIFT = 'audio-wall-shift';                     // Wall shifting sound (placeholder)

// Moving wall colors
export const COLOR_MOVING_WALL_GLOW = 0x886644;            // Warm glow on walls about to shift

// ═══════════════════════════════════════════════════════════════════
// Secret Passages (level 3+)
// ═══════════════════════════════════════════════════════════════════
export const SECRET_PASSAGE_MIN_LEVEL = 3;                 // Secret passages appear from level 3+
export const SECRET_PASSAGE_FRACTION = 0.008;              // ~0.8% of interior walls become secrets
export const SECRET_PASSAGE_MIN_COUNT = 1;                 // Minimum secret passages per eligible level
export const SECRET_PASSAGE_MAX_COUNT = 4;                 // Maximum secret passages per level
export const SECRET_PASSAGE_PUSH_COUNT = 3;                // Pushes against the wall to reveal
export const SECRET_PASSAGE_PUSH_INTERVAL = 800;           // Max ms between pushes to count as sequence

// Secret passage texture key
export const TEX_SECRET_WALL = 'secret-wall';              // Subtly different wall texture (crack)

// Secret passage audio
export const AUDIO_SECRET_PASSAGE_OPEN = 'audio-secret-passage-open';   // Stone grinding open (placeholder)
export const AUDIO_SECRET_PASSAGE_HINT = 'audio-secret-passage-hint';   // Subtle hint sound (placeholder)

// Secret passage colors
export const COLOR_SECRET_WALL_CRACK = 0x999999;           // Subtle lighter crack line

// ═══════════════════════════════════════════════════════════════════
// Shadow Walkers (level 7+)
// ═══════════════════════════════════════════════════════════════════
export const SHADOW_WALKER_MIN_LEVEL = 7;                  // Shadow Walkers appear from level 7+
export const SHADOW_WALKER_COUNT_BASE = 1;                 // 1 shadow at levels 7-9
export const SHADOW_WALKER_COUNT_LEVEL_THRESHOLD = 10;     // 2 shadows from level 10+
export const SHADOW_WALKER_COUNT_MAX = 2;                  // Max shadows per level
export const SHADOW_WALKER_SIZE = 22;                      // Shadow sprite size in pixels
export const SHADOW_WALKER_PATROL_TWEEN_MS = 280;         // ms per cell-to-cell tween during patrol
export const SHADOW_WALKER_CHASE_TWEEN_MS = 180;          // ms per cell-to-cell tween during chase
export const SHADOW_WALKER_VISION_RANGE = 5;               // Tiles line-of-sight detection range
export const SHADOW_WALKER_DAMAGE = 15;                    // HP damage on contact
export const SHADOW_WALKER_COIN_STEAL = 3;                 // Coins stolen on contact
export const SHADOW_WALKER_HIT_COOLDOWN = 2000;            // ms invulnerability after hitting player before next hit
export const SHADOW_WALKER_RETREAT_CELLS = 3;              // Cells to retreat after hitting the player
export const SHADOW_WALKER_RETREAT_TWEEN_MS = 200;         // ms per cell-to-cell tween during retreat
export const SHADOW_WALKER_CHASE_DURATION = 15000;         // ms of active chase before shadow gives up and returns to spawn
export const SHADOW_WALKER_RETURN_TWEEN_MS = 220;          // ms per cell-to-cell tween while returning to spawn zone
export const SHADOW_WALKER_DESPAWN_DURATION = 500;         // ms fade-out (used only for final destruction)
export const SHADOW_WALKER_DIRECTION_CHANGE_INTERVAL = 3000; // ms between random direction changes during patrol
export const SHADOW_WALKER_MIN_SPAWN_DISTANCE = 8;         // Min tiles from player for spawn position

// Shadow Walker audio
export const AUDIO_SHADOW_WALKER_SPOT = 'audio-shadow-walker-spot';     // Whisper when shadow spots player (placeholder)
export const AUDIO_SHADOW_WALKER_HIT = 'audio-shadow-walker-hit';       // Shadow contact damage (placeholder)
export const AUDIO_SHADOW_WALKER_DESPAWN = 'audio-shadow-walker-despawn'; // Shadow dissipating (placeholder)

// Shadow Walker colors
export const COLOR_SHADOW_WALKER = 0x111122;               // Very dark blue-black
export const COLOR_SHADOW_WALKER_EYES = 0x8800aa;          // Faint purple eyes
export const COLOR_SHADOW_WALKER_TRAIL = 0x110011;         // Dark trail particles

// Shadow Walker texture key
export const TEX_SHADOW_WALKER = 'shadow-walker';

// ═══════════════════════════════════════════════════════════════════
// Bat Nest (level 4+)
// ═══════════════════════════════════════════════════════════════════
export const BAT_NEST_MIN_LEVEL = 4;                       // Bat nests appear from level 4+
export const BAT_NEST_SPAWN_CHANCE = 0.60;                 // 60% chance of a nest per eligible level
export const BAT_NEST_ACTIVATION_RADIUS = 3;               // Tiles distance to trigger nest activation
export const BAT_NEST_BATS_COUNT = 3;                      // Number of bats released on activation
export const BAT_NEST_BAT_SPAWN_DELAY = 300;               // ms delay between each bat spawning from nest
export const BAT_NEST_COOLDOWN = 12000;                    // ms cooldown between activations
export const BAT_NEST_HAMMER_HITS = 3;                     // Hammer hits (B combos) to destroy nest
export const BAT_NEST_SIZE = 20;                           // Nest sprite size in pixels
export const BAT_NEST_MIN_SPAWN_DISTANCE = 6;              // Min tiles from player start for nest placement
export const BAT_NEST_TRAVERSE_DAMAGE_MULT = 2;            // Damage multiplier when walking through a nest
export const BAT_NEST_TRAVERSE_COIN_MULT = 2;              // Coin loss multiplier when walking through a nest
export const BAT_NEST_IDLE_DRAIN_INTERVAL = 1200;          // ms between idle damage ticks while standing on nest
export const BAT_NEST_IDLE_DRAIN_AMOUNT = 3;               // HP per idle drain tick on nest cell
export const BAT_NEST_MINI_BATS_COUNT = 3;                 // Number of mini-bat sprites orbiting a nest
export const BAT_NEST_MINI_BAT_SIZE = 7;                   // Mini-bat sprite display size in pixels
export const BAT_NEST_FLAP_SPEED = 200;                    // ms per wing-flap toggle for mini-bats
export const BAT_NEST_ORBIT_RADIUS = 10;                   // Base orbit radius for mini-bats (px)
export const BAT_NEST_FLY_OUT_CHANCE = 0.003;              // Per-frame chance a mini-bat flies out of the nest
export const BAT_NEST_FLY_OUT_DISTANCE = 40;               // Max distance (px) a mini-bat flies from nest center
export const BAT_NEST_FLY_OUT_DURATION = 2500;             // ms for a mini-bat fly-out excursion round-trip
export const BAT_NEST_ATTACK_COUNT = 2;                    // Number of bats that immediately attack on activation
export const BAT_NEST_EMERGE_SPEED = 110;                  // px/s outward speed during nest emergence phase
export const BAT_NEST_EMERGE_DISTANCE = 2.5;               // Tiles distance bats fly outward from nest before normal AI
export const BAT_NEST_REATTACK_INTERVAL = 4000;            // ms between re-attack checks while player is near nest

// Bat Nest audio
export const AUDIO_BAT_NEST_ACTIVATE = 'audio-bat-nest-activate';    // Swarm sound (placeholder)
export const AUDIO_BAT_NEST_DESTROY = 'audio-bat-nest-destroy';      // Nest destruction (placeholder)

// Bat Nest colors
export const COLOR_BAT_NEST = 0x332211;                    // Dark brown nest body
export const COLOR_BAT_NEST_EYES = 0xff0000;               // Red eyes in nest (matching bat eyes)
export const COLOR_BAT_NEST_CRUMBLED = 0x444444;           // Gray for crumbled wall debris around nest

// Bat Nest texture key
export const TEX_BAT_NEST = 'bat-nest';
export const TEX_BAT_NEST_DESTROYED = 'bat-nest-destroyed';

// ---------------------------------------------------------------
// Mini-map
// ---------------------------------------------------------------
export const MINIMAP_WIDTH = 130;                              // Mini-map widget width in pixels
export const MINIMAP_HEIGHT = 100;                             // Mini-map widget height in pixels
export const MINIMAP_MARGIN = 6;                               // Margin from border edges
export const MINIMAP_BG_ALPHA = 0.75;                          // Background panel opacity
export const MINIMAP_BORDER_COLOR = 0xffffff;                  // Border color (white)
export const MINIMAP_BORDER_ALPHA = 0.3;                       // Border opacity
export const MINIMAP_WALL_COLOR = 0x333333;                    // Visited wall color (dark gray)
export const MINIMAP_PATH_COLOR = 0x888888;                    // Visited path color (dim gray)
export const MINIMAP_PLAYER_COLOR = 0xffffff;                  // Player dot color (white)
export const MINIMAP_EXIT_COLOR = 0xffd700;                    // Exit dot color (gold, only with compass)
export const MINIMAP_WATER_COLOR = 0x4488cc;                   // Water hazard color (blue)
export const MINIMAP_LAVA_COLOR = 0xff6622;                    // Lava hazard color (orange-red)
export const MINIMAP_POISON_COLOR = 0x44cc44;                  // Poison hazard color (green)
export const MINIMAP_SPIKE_COLOR = 0xcccccc;                   // Spike hazard color (light gray)
export const MINIMAP_PLAYER_DOT_SIZE = 2;                      // Player dot radius in pixels
export const MINIMAP_EXIT_DOT_SIZE = 2;                        // Exit dot radius in pixels

// ---------------------------------------------------------------
// Direction Trail
// ---------------------------------------------------------------
export const TRAIL_LENGTH = 5;                                 // Number of recent positions to show
export const TRAIL_DOT_SIZE = 2;                               // Trail dot radius in pixels
export const TRAIL_BASE_ALPHA = 0.5;                           // Newest trail dot opacity
export const TRAIL_FADE_STEP = 0.08;                           // Alpha reduction per older position
export const TRAIL_COLOR = 0xaaaaaa;                           // Trail dot color (light gray)
